import { Box, IconButton, Menu, MenuItem, useTheme } from '@mui/material';
import { MouseEvent, useId, useState } from 'react';
import { popupMenuStyles, popupMenuProps } from './PopupMenu.styles';
import DownwardTriangleIcon from './images/downwardTriangle.svg';

export type PopupMenuItemId = string | number;

export interface PopupMenuItem<TId extends PopupMenuItemId = PopupMenuItemId> {
  id: TId;
  name: string;
}

export interface PopupMenuProps<TId extends PopupMenuItemId = PopupMenuItemId> {
  items: PopupMenuItem<TId>[];
  onSelect: (selectedId: TId) => void;
  ariaLabel?: string;
  disabled?: boolean;
}

export const PopupMenu = <TId extends PopupMenuItemId>({
  items,
  onSelect,
  ariaLabel = 'Open menu',
  disabled = false,
}: PopupMenuProps<TId>) => {
  const theme = useTheme();
  const triggerId = useId();
  const menuId = `${triggerId}-menu`;
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorElement);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleSelect = (item: PopupMenuItem<TId>) => {
    onSelect(item.id);
    handleClose();
  };

  return (
    <Box component="span" sx={popupMenuStyles.container(theme)}>
      <IconButton
        id={triggerId}
        aria-label={ariaLabel}
        aria-controls={isOpen ? menuId : undefined}
        aria-haspopup="menu"
        aria-expanded={isOpen ? 'true' : undefined}
        disabled={disabled}
        onClick={handleOpen}
        size="small"
        sx={popupMenuStyles.trigger(theme)}
      >
        <Box
          component="img"
          src={DownwardTriangleIcon}
          alt=""
          aria-hidden={true}
          sx={popupMenuStyles.icon(theme)}
        />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorElement}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={popupMenuProps.anchorOrigin}
        transformOrigin={popupMenuProps.transformOrigin}
        MenuListProps={{
          'aria-labelledby': triggerId,
        }}
        PaperProps={{
          sx: popupMenuStyles.paper(theme),
        }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => handleSelect(item)}
            sx={popupMenuStyles.menuItem(theme)}
          >
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default PopupMenu;
